import { Node } from "reactflow";
import { Colors } from "../styles/colors";
import { JSX } from "react";
import { TreeCheckboxSelectionKeys } from "primereact/tree";

export type Flow = {
  id: string;
  name: string;
  email: string;
  timestamp: string;
  role: string;
  favourite: boolean;
  nodes: Node<NodeData>[];
  edges: MapEdge[];
};

export type Collaborators = {
  id: string;
  owner: string;
  createdAt: string;
  modifiedAt: string;
  collaborators: string[];
};

export type Feedback = {
  id: string;
  email: string;
  feedback: string;
  timestamp: string;
};

export type Logs = {
  id: string;
  user: string;
  error: string;
  timestamp: string;
};

export type Unlockedfeatures = {
  id: string;
  startDate: string;
  endDate: string;
};

export type User = {
  id: string;
  email: string;
  name: string;
  plan: string;
  showWelcome: boolean;
  useLocalData: boolean;
  createdAt: string;
  modifiedAt: string;
  insights: TreeCheckboxSelectionKeys;
  settings: {
    presentWithShowHideButtons: boolean;
    currency: string;
    showWhyTag?: boolean;
    showStartupDialog?: boolean;
    showAIPromptOnNewMap?: boolean;
    aiModel?: string;
  };
};

export type SystemTemplate = {
  id: string;
  logo: string;
  name: string;
  description: string | JSX.Element;
};

export type Integration = {
  id: string;
  logo: string;
  name: string;
  description: string | JSX.Element;
};

export type CustomProperty = {
  name: string;
  type:
    | "number"
    | "string"
    | "boolean"
    | "date"
    | "calculated"
    | "list"
    | "currency";
  value: any;
  visible: boolean;
};

export type Links = {
  title: string;
  url: string;
};

export type Event = {
  id: string;
  nodeTitle: string;
  title: string;
  description: string;
  date: Date | string | undefined;
  icon: string;
  color: string;
};

export type Address = {
  id: string;
  nodeTitle: string;
  name: string;
  street: string;
  zip_code: string;
  city: string;
  Phone: string;
  email: string;
  vat: string;
  employees: string;
  notes: string;
  latitude?: number;
  longitude?: number;
};

export type Task = {
  id: string | number;
  title: string;
  nodeTitle: string;
  status: string;
  summary?: string;
  priority?: string;
  tags?: string;
  assignee?: string;
  estimate?: number;
  rankId?: number;
  color?: string;
  className?: string;
  startDate?: Date | string;
  endDate?: Date | string;
};

export type values = {
  title: string;
  value: string;
};

export type nodeType =
  | "task"
  | "resource"
  | "location"
  | "risk"
  | "decision"
  | "image";

export type Resource = {
  id?: string;
  name: string;
  role: string;
  department: string;
  email: string;
  phone: string;
  availability: number; // Percentage 0-100
  skills: string;
  notes: string;
};

export type Risk = {
  id?: string;
  name: string;
  description: string;
  category: string;
  impact: string; // High, Medium, Low
  likelihood: string; // High, Medium, Low
  severity: string; // Critical, High, Medium, Low (calculated or manual)
  mitigation: string;
  owner: string;
  status: string; // Open, Mitigating, Closed, Accepted
  identifiedDate?: Date | string;
  reviewDate?: Date | string;
};

export type Decision = {
  id?: string;
  name: string;
  description: string;
  category: string; // Strategic, Tactical, Operational
  status: string; // Pending, Approved, Rejected, Deferred
  decisionMaker: string;
  stakeholders: string;
  criteria: string;
  alternatives: string;
  selectedOption: string;
  rationale: string;
  implications: string;
  decisionDate?: Date | string;
  reviewDate?: Date | string;
};

export type NodeData = {
  label: string;
  icon: string;
  object: string;
  objectType: string;
  objectWidth: number;
  objectHeight: number;
  notes: string;
  complete: boolean;
  status: string;
  flagged: string;
  color: string;
  backgroundColor: string;
  presentationOrder: string;
  priority: string;
  links: Links[];
  value: string;
  values: values[];
  rollupValues: values[];
  customProperties: CustomProperty[];
  event: Event;
  address: Address;
  task: Task;
  risk?: Risk;
  decision?: Decision;
  ms365Document: MS365Document;
  why?: string;
  type?: nodeType;
  resource?: Resource;
};

export type MapEdge = {
  id: string;
  source: string;
  target: string;
  type: string;
};

export type ColorScheme = typeof Colors;

type OneNotePageLink = {
  oneNoteClientUrl: {
    href: string;
  };
  oneNoteWebUrl: {
    href: string;
  };
};

type OneNoteSection = {
  id: string;
  displayName: string;
  self: string;
};

export type OneNotePage = {
  content: string | undefined;
  id: string;
  self: string;
  createdDateTime: string;
  title: string;
  createdByAppId: string;
  contentUrl: string;
  lastModifiedDateTime: string;
  links: OneNotePageLink;
  "parentSection@odata.context": string;
  parentSection: OneNoteSection;
};

export type ResponseType = {
  success: boolean;
  description: string;
};

export type OneNotePages = OneNotePage[];

export const viewTypes = {
  FocusMap: {
    label: "Visualize",
    type: "goal",
    description:
      "Map your ideas and goals as a visual network of connected thoughts.",
  },
  Wheel: {
    label: "Scope",
    type: "layer",
    description:
      "Explore the structure and depth of your ideas in a layered sunburst view.",
  },
  TimeLine: {
    label: "Organize",
    type: "time",
    description:
      "See your tasks and events on a timeline to plan and track progress over time.",
  },
  Grid: { label: "Grid", type: "grid", description: "Grid view" },
  WorldMap: {
    label: "Locations",
    type: "place",
    description:
      "Visualize tasks and events connected to physical locations on the map.",
  },
  Kanban: {
    label: "Realize",
    type: "status",
    description:
      "Manage and execute your tasks in a Kanban-style board from ToDo to Done.",
  },
};

export type Template = {
  id: string;
  name: string;
  email: string;
  timestamp: string;
  logo: string;
  role: string;
  description: string | JSX.Element;
  author: string;
  nodes: Node<NodeData>[];
  edges: MapEdge[];
};

export type WheelData = {
  name: string;
  step: number; // 0–4
  group:
    | "Kunderelationer"
    | "Virksomhedsdrift"
    | "Organisation"
    | "Forretningskoncept";
  descriptions?: string[];
};

export type FlowWheelProps = {
  name: string;
  nodes: Node<NodeData>[];
  edges: MapEdge[];
  onClickNode?: (node: NodeData) => void;
  onUpdateData?: (updated: NodeData[]) => void;
};

export type SunburstChartProps = {
  data: WheelData[];
  width?: number;
  height?: number;
  onClickNode?: (node: WheelData) => void;
  onUpdateData?: (updated: WheelData[]) => void;
};

export type LawyerData = {
  name: string;
  cvrData: any[];
  darData: any[];
  bbrData: any[];
  svrData: any[];
  ejfData: any[];
  vurData: any[];
  cprData: any[];
  formattedCvrData: string;
  formattedDarData: string;
  formattedBbrData: string;
  formattedSvrData: string;
  formattedEjfData: string;
  formattedVurData: string;
  formattedCprData: string;
};

export interface KanbanCard {
  id: string;
  title: string;
  description: string;
  label: string;
  metadata: {
    status: string;
    type: string;
    data: any;
  };
}

export interface KanbanLane {
  id: string;
  title: string;
  label: string;
  cards: KanbanCard[];
}

export interface KanbanData {
  lanes: KanbanLane[];
}

export interface KanbanCardData {
  id: string | number;
  title?: string;
  parentLabel?: string;
  nodeTitle?: string;
  status?: string;
  summary?: string;
  priority?: string;
  tags?: string;
  assignee?: string;
  estimate?: number;
  rankId?: number;
  color?: string;
  className?: string;
  startDate?: Date;
  endDate?: Date;
  customProperties?: CustomProperty[];
}

export type MS365Document = {
  id: string;
  type:
    | "email"
    | "event"
    | "chat"
    | "docx"
    | "xlsx"
    | "pptx"
    | "pdf"
    | "onenote"
    | "teams"
    | "sharepoint"
    | "attachment"
    | "contact"
    | "todo"
    | "notes"
    | "journal"
    | "genericfile";
  title: string;
  timestamp: string;
  summary?: string;
  link?: string;
  metadata?: any;
  hasAttachments?: boolean;
  attachments?: any[];
  // Document-specific fields
  driveType?: "onedrive" | "sharepoint";
  fileType?: string;
  fileSize?: number;
  lastModifiedBy?: string;
  // Extended for OneDrive/SharePoint support
  documentLocation?: string; // e.g., "OneDrive", "SharePoint"
  documentPath?: string; // e.g., "/Documents/Project/file.docx"
  previewUrl?: string; // link to preview the document
  siteId?: string; // SharePoint site id if applicable
  driveId?: string; // Drive id for OneDrive/SharePoint
  webUrl?: string; // Direct web link to the document
  objectWidth?: number; // Width for display
  objectHeight?: number; // Height for display
  thumbnails?: {
    id: string;
    large?: { url: string; width: number; height: number };
    medium?: { url: string; width: number; height: number };
    small?: { url: string; width: number; height: number };
    source?: { url: string; width: number; height: number };
  }[];
  relatedIds: string[];
  conversationId?: string; // For emails and chats, to link related messages
  body?: {
    contentType: "text" | "html";
    content: string;
  }; // Full body content for emails and meetings
};

export type MS365DocumentFilter = {
  types?: MS365Document["type"][];
  dateRange?: { start: string; end: string };
  hasAttachments?: boolean;
  searchText?: string;
  driveType?: MS365Document["driveType"][];
  status?: string;
  maxResults?: number;
  deleteUnselected?: boolean;
  useCopilot?: boolean;
  outputMode?: string;
};

// ReactFlow node and edge type configurations
export type NodeTypes = {
  mindmap: any;
  nodeItem: any;
  center: any;
  image: any;
};

export type EdgeTypes = {
  mindmap: any;
  edge: any;
  center: any;
  crossline: any;
  image: any;
};
